import { createElement, useRef, useEffect, useState, forwardRef } from 'react';
import {
  TokenInputStatus,
  TokenInputProps,
  UseTokenInputState,
  DraggableTokenProps,
} from './types';
import { Tokenizer, TokenizerToken } from '../Tokenizer';
import { BaseInput, getInputThemeTokens } from '../Input/__internal__/BaseInput';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { MessagePopover } from '../Input/__internal__/MessagePopover';
import { postfixTestId } from '../../util/testId';
import { Icon } from '../Icon';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
import useOnFocusOut from '../../hooks/useOnFocusOut';
import { useDirectSpeech } from '../DirectSpeech/DirectSpeechProvider';
import bundle from './NovaComponents-TokenInput-gui-icu.properties';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useMounted } from '../../hooks/useMounted';
import { Flow } from '../Layouts/Flow';
import { Button } from '../Button';
import { useFormatter } from '../../locale/formatting';
import { getLogger } from '@sas/nova-core/logging';
import { InputProps } from '../Input';
import { useReactDndConstructs as useDragAndDropHooks } from '../../dnd/ReactDndContext';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { getDndStyleTokens } from '../../style/reactDnd/DndStyleTokens';
import { TokenDragPreview } from '../Token/__internal__/TokenDragPreview';
import cx from 'classnames';
import cn from './TokenInput.css.json';
import './TokenInput.css';

export const TokenDragType = 'NOVA_TOKEN';

const useTextLocalizer = createUseTextLocalizer(bundle);
const useTokenInputDndPrefixId = createUseHtmlId('TokenInput-dndPrefix');
const logger = getLogger('TokenInput');
/**
 * This hook can be used to manage the state for a token input component.
 * This is the preferred way to set TokenInput's required props.
 * @example
 * ```tsx
 * const tokenInputProps = useTokenInputState(initialTokens);
 * // ...
 * return <TokenInput {...tokenInputProps} />
 * ```
 */
export const useTokenInputState: UseTokenInputState = (initialTokens, onCreateToken) => {
  const [tokens, setTokens] = useState(initialTokens);
  const [inputValue, setInputValue] = useState('');

  const onAdd: TokenInputProps['onAdd'] = (value) => {
    const newToken = onCreateToken?.(value) ?? { value };
    setTokens((prevTokens) => [...prevTokens, newToken]);
  };
  const onRemove: TokenInputProps['onRemove'] = (toRemoveToken) => {
    setTokens((prevTokens) => prevTokens.filter((token) => token !== toRemoveToken));
  };
  return {
    tokens,
    setTokens,
    onAdd,
    onRemove,
    inputValue,
    onInputChange: ({ value }) => setInputValue(value),
  };
};

export const TokenInput = forwardRef<HTMLInputElement, TokenInputProps>(function TokenInput(
  { allowDrop = false, ...props },
  ref
) {
  const localize = useTextLocalizer();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasReceivedFocus, setHasReceivedFocus] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const directSpeech = useDirectSpeech();
  const [duplicateValue, setDuplicateValue] = useState('');
  const [showUncommittedError, setShowUncommittedError] = useState(false);
  const [showNotMatchSuggestionError, setShowNotMatchSuggestionError] = useState(false);
  const [showBlankValueError, setShowBlankValueError] = useState(false);
  const formatter = useFormatter();
  const maxNumTokensReached =
    props.maxNumTokens !== undefined && props.tokens.length >= props.maxNumTokens;
  const showMaxNumTokensReachedError = maxNumTokensReached && props.inputValue.length > 0;
  const mountedRef = useMounted();
  const { useDrop } = useDragAndDropHooks();
  const defaultDndPrefix = useTokenInputDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const tokens = props.tokens.map<TokenizerToken>((token) => {
    const tokenizerToken = token as TokenizerToken; // use the same object because it is used for deleting tokens
    if (props.menuSections || tokenizerToken.menuSections !== undefined) {
      tokenizerToken.menuSections = props.menuSections;
    }
    if (props.readOnly || tokenizerToken.readOnly !== undefined) {
      tokenizerToken.readOnly = props.readOnly;
    }
    if (props.allowDrag) {
      tokenizerToken.allowDrag = tokenizerToken.allowDrag ?? props.allowDrag;
    }
    if (props.dndPrefix || tokenizerToken.dndPrefix !== undefined || tokenizerToken.allowDrag) {
      tokenizerToken.dndPrefix = dndPrefix;
    }
    return tokenizerToken;
  });

  const onRemoveTokens = (indices: number[]) => {
    if (tokens.length === 0 || indices.length === 0) return;

    // If no token left after removing, set the focus on the input
    if (tokens.length === 1) {
      inputRef.current?.focus();
    }
    // The Tokenizer in TokenInput only removes one token at a time,
    // so assume the indices has length of 1
    const tokenToRemove = tokens[indices[0]];
    props.onChange?.(props.tokens.filter((token) => token !== tokenToRemove));
    props.onRemove?.(tokenToRemove);
  };

  const getDefaultStatus = (): TokenInputStatus | undefined => {
    if (showMaxNumTokensReachedError && props.maxNumTokens !== undefined) {
      return {
        type: 'alert',
        message: localize('maxNumTokensReached.error.msg', {
          maximum: formatter?.formatNumeric(props.maxNumTokens) ?? props.maxNumTokens.toString(),
        }),
      };
    }

    if (showBlankValueError) {
      return {
        type: 'alert',
        message: localize('blankValue.error.msg'),
      };
    }

    if (duplicateValue) {
      return {
        type: 'alert',
        message:
          props.duplicateWarningMessage?.(duplicateValue) ??
          localize('duplicateToken.error.msg', { name: duplicateValue }),
      };
    }

    if (showNotMatchSuggestionError) {
      return {
        type: 'alert',
        message: localize('mustMatchSuggestion.error.msg'),
      };
    }

    if (showUncommittedError) {
      return {
        type: 'alert',
        message: localize('uncommittedValue.error.msg'),
      };
    }

    return undefined;
  };

  const status = props.status ?? getDefaultStatus();

  useEffect(() => {
    if (props.autofocus) {
      inputRef.current?.focus();
    }
  }, [props.autofocus]);

  useEffect(() => {
    // Clear the error/warning message when input's value changes
    setDuplicateValue('');
    setShowBlankValueError(false);
    setShowUncommittedError(false);
    setShowNotMatchSuggestionError(false);
  }, [props.inputValue]);

  const dt = useDesignTokens();

  // Outline Focus Indicator
  const style = {
    ...renderInlineCssVars(getTokenInputThemeTokens(dt, status?.type), 'token-input'),
    ...renderInlineCssVars(
      {
        outline: dt.themeVariant !== 'high-contrast' ? 'none' : 'var(--focus-light-bg)',
        'outline-offset': 'calc(-1 * var(--focus-width) - 2px)',
      },
      'token-input-focus'
    ),
  };

  const onMouseEnter = () => {
    setIsHovered(true);
  };
  const onMouseLeave = () => {
    setIsHovered(false);
  };

  const stateMessage =
    hasReceivedFocus && status
      ? `${props.ariaLabel ?? ''}, ${localize(
          status.type === 'alert' ? 'errorState.aria' : 'warningState.aria'
        )}`
      : undefined;
  const messageToAnnouce = isFocused ? status?.message : stateMessage;

  useEffect(() => {
    if (messageToAnnouce) {
      directSpeech?.notify?.(messageToAnnouce);
    }
  }, [directSpeech, messageToAnnouce]);

  useEffect(() => {
    if (props.maxNumTokens && props.tokens.length > props.maxNumTokens) {
      logger.warn(
        `The maximum number of items is ${props.maxNumTokens}. The rest of the items could not be loaded.`
      );
    }
  }, [props.maxNumTokens, props.tokens.length]);

  const { onFocus: onInputFocus, onBlur: onInputBlur } = useOnFocusOut(() => {
    if (!mountedRef.current) return;
    setIsInputFocused(false);
    if (props.inputValue.length === 0) return;

    if (props.inputValue.trim().length === 0) {
      setShowBlankValueError(true);
    } else {
      setShowUncommittedError(true);
    }
  });
  const { onFocus, onBlur } = useOnFocusOut(() => {
    if (!mountedRef.current) return;
    setIsFocused(false);
    setHasReceivedFocus(true);
  });

  const isDuplicate =
    props.isDuplicate ?? ((value: string) => tokens.some((token) => token.value === value));
  const suggestionItems: InputProps['suggestionItems'] = props.preventDuplicates
    ? props.suggestionItems?.filter(({ text }) => text && !isDuplicate(text))
    : props.suggestionItems;

  const canDrop = (item: DraggableTokenProps) =>
    allowDrop &&
    item.dndPrefix !== dndPrefix &&
    (!props.allowDropDndPrefixes || props.allowDropDndPrefixes.includes(item.dndPrefix));

  const [{ isOverTokenInputDropZone }, tokenInputDropZoneConnect] = useDrop({
    accept: [TokenDragType],
    canDrop,
    collect(monitor) {
      return {
        isOverTokenInputDropZone: monitor.isOver() && canDrop(monitor.getItem()),
      };
    },
    drop: (item: DraggableTokenProps, monitor) => {
      if (!monitor.didDrop() && canDrop(item)) {
        props.onDrop?.({
          droppedItem: item,
          monitor,
        });
      }
    },
  });
  tokenInputDropZoneConnect(containerRef);
  const dndTokens = getDndStyleTokens(dt);

  return (
    <Flow
      role="grid"
      orientation="horizontal"
      spacing="none"
      aria-label={props.ariaLabel}
      aria-labelledby={props.ariaLabelledBy}
      aria-describedby={props.ariaDescribedBy}
      style={{ ...style, ...dndTokens, ...props.style }}
      className={cx(cn.outerContainer, props.className)}
    >
      <div
        ref={containerRef}
        className={cx(
          cn.container,
          isInputFocused && cn.focusVisible,
          // Always save space for the icon even when it is not visible to avoid re-layout of tokens when focused
          status && cn.hasStatusIcon,
          props.readOnly && cn.readOnly,
          isOverTokenInputDropZone && cn.dragOver
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onBlur={onBlur}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus();
          if (
            e.currentTarget.children[0] === e.target &&
            document.activeElement !== inputRef.current
          ) {
            // Move focus to the input if the Tokenizer container receives focus
            // This usually happens when users click the blank space in TokenInput
            inputRef.current?.focus();
          }
        }}
      >
        <Tokenizer
          testId={postfixTestId(props.testId, 'tokenizer')}
          tokens={props.maxNumTokens ? tokens.slice(0, props.maxNumTokens) : tokens}
          onRemoveTokens={onRemoveTokens}
          wrap
          role=""
          className={cx(cn.tokenizer, props.tokenizerClassName)}
          style={props.tokenizerStyle}
        >
          {props.readOnly ? null : (
            <div role="gridcell" className={cn.inputFieldContainer}>
              <BaseInput
                id={props.id}
                className={cn.inputField}
                ref={(element) => {
                  inputRef.current = element;
                  setRef(ref, element);
                }}
                value={props.inputValue}
                filterType={props.inputFilterType ?? 'contains'}
                // Do not show the suggestion list if the tokens maximum is reached
                // to avoid showing two popovers: inline error message and suggestion list
                suggestionItems={maxNumTokensReached ? undefined : suggestionItems}
                hideBorder
                ariaLabel={props.inputAriaLabel ?? localize('input.default.aria')}
                ariaInvalid={status?.type === 'alert'}
                placeholder={tokens.length === 0 ? props.inputPlaceholder : undefined}
                onChange={props.onInputChange}
                onCommit={({ value, reason }) => {
                  if (value.length === 0 || reason === 'blur' || maxNumTokensReached) return;

                  const trimmedValue = value.trim();

                  // Check for blank values
                  if (trimmedValue.length === 0) {
                    setShowBlankValueError(true);
                    return;
                  }

                  // Check for duplicate values
                  if (props.preventDuplicates && isDuplicate(trimmedValue)) {
                    setDuplicateValue(trimmedValue);
                    return;
                  }

                  // Check for values matching suggestions
                  if (
                    props.mustMatchSuggestion &&
                    reason !== 'suggestion' &&
                    !(
                      reason === 'enter' &&
                      props.suggestionItems?.some(({ text }) => text === trimmedValue)
                    )
                  ) {
                    setShowNotMatchSuggestionError(true);
                    return;
                  }

                  props.onChange?.([...props.tokens, { value: trimmedValue }]);
                  props.onAdd?.(trimmedValue);
                  props.onInputChange({ value: '', reason: 'onAdd' });
                }}
                onBlur={onInputBlur}
                onFocus={() => {
                  setIsInputFocused(true);
                  onInputFocus();
                }}
                commitOnEnterInDialog
              />
            </div>
          )}
        </Tokenizer>
        {!isFocused && status ? (
          <div className={cn.inputIcon} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <Icon
              icon={status.type === 'alert' ? ErrorStatus : WarningStatus}
              color={status.type}
            />
          </div>
        ) : null}
        {status ? (
          <MessagePopover
            status={status.type}
            referenceElementRef={containerRef}
            message={status.message}
            hidden={!isFocused && !isHovered}
            testId={postfixTestId(props.testId, 'messagePopper')}
          />
        ) : null}
      </div>
      {props.buttonProps ? (
        <div role="row">
          <div role="gridcell">
            <Button {...props.buttonProps} className={cx(props.buttonProps, cn.button)} />
          </div>
        </div>
      ) : null}
      {props.allowDrag ? (
        <TokenDragPreview
          dndPrefix={dndPrefix}
          getTokenProps={(index: number) => {
            const token = tokens[index];
            if (!token) return null;
            return {
              value: token.value,
              icon: token.icon,
              tooltip: token.tooltip,
              menuSections: token.menuSections,
              includeDropdown: !!token.menuSections,
              readOnly: token.readOnly,
            };
          }}
        />
      ) : null}
    </Flow>
  );
});

const getTokenInputThemeTokens = (
  dt: DesignTokens,
  status: TokenInputStatus['type'] | undefined
) => {
  const inputTokens = getInputThemeTokens(dt, status);
  return {
    bd: inputTokens.bd,
    bg: inputTokens.bg,
    fg: inputTokens.fg,
    'active-bd': inputTokens['focus-bd'] ?? inputTokens.bd,
    'spacing-xs': dt.spacing.core.xs,
  };
};
